// Mirrored in the prompts docs/ai/rich-widgets.md, suggested-actions.md and follow-up-questions.md:
// change them together. `parseAnswerWidget` never throws; what it cannot read stays a code block.

import { EntryType, Octopus } from '@maximizer/core/shared/domain';
import { parseDateOnlyLocal } from '@maximizer/shared/util';
import { AiMarkupUtils } from '../classes/ai-markup-utils';
import { isRecord } from '../classes/value-checks';

export const ANSWER_WIDGET_LANGUAGES = [
  'nxchart',
  'nxcard',
  'nxchiplist',
  'nxtimeline',
  'nxstats',
  'nxstack',
  'nxprofile',
  'nxlist',
  'nxfacts',
  'nxtable',
  'nxsources',
  'nxactions',
  'nxfollowups',
] as const;

export type AnswerWidgetLanguage = (typeof ANSWER_WIDGET_LANGUAGES)[number];

/** Charts get one hue per series from a fixed palette, so at most this many series are drawn. */
export const MAX_CHART_SERIES = 6;
export const MAX_CHART_POINTS = 50;
/** Slices beyond the palette are folded into an "Other" slice by the chart component. */
export const MAX_PIE_SLICES = 12;
export const MAX_CHIPS = 20;
export const MAX_CARDS = 6;
/**
 * Timeline bounds. Two is the floor because one dated fact is a sentence, not a sequence, and the
 * shape only says something once there is a second event to sit it against.
 */
export const MIN_TIMELINE_EVENTS = 2;
export const MAX_TIMELINE_EVENTS = 8;
/** Stat bounds: one figure belongs in the prose, and a row past six stops reading as a row. */
export const MIN_STATS = 2;
export const MAX_STATS = 6;
/** A stack of one is the widget itself; three is all the chat panel splits into and stays read. */
export const MIN_STACK_ITEMS = 2;
export const MAX_STACK_ITEMS = 3;
/** A card group lays out in 1 to this many columns; past that the cards get too narrow to read. */
export const MAX_CARD_COLUMNS = 4;
/** A profile's facts share one line; past six they wrap and stop reading as a line. */
export const MAX_PROFILE_DETAILS = 6;
/** List bounds: one row with a status is a sentence, and a checklist past ten is no longer scanned. */
export const MIN_LIST_ITEMS = 2;
export const MAX_LIST_ITEMS = 10;
/** Fact bounds: one fact is a sentence, and past twelve the grid is a record view, not an answer. */
export const MIN_FACTS = 2;
export const MAX_FACTS = 12;
/** Table bounds: two columns make a table, and six is what the answer's width aligns figures in. */
export const MIN_TABLE_COLUMNS = 2;
export const MAX_TABLE_COLUMNS = 6;
export const MIN_TABLE_ROWS = 2;
export const MAX_TABLE_ROWS = 20;
/** Web sources under an answer; past ten the list is longer than what it supports. */
export const MAX_SOURCES = 10;
/** A source's date is copied from the search hit as written, in whatever form it came. */
export const MAX_SOURCE_DATE_LENGTH = 40;
/** Suggested actions under an answer: past three they stop being the next steps. */
export const MAX_ACTIONS = 3;
/** How far ahead an action may be set from today; the data never calls for more than a year. */
export const MAX_ACTION_IN_DAYS = 365;
/** Questions to ask next: past three the reader is choosing from a menu, not following a thread. */
export const MAX_FOLLOW_UPS = 3;
/** A chip asks exactly what it shows, so a longer question is dropped rather than cut. */
export const MAX_FOLLOW_UP_LENGTH = 200;
export const MAX_LABEL_LENGTH = 80;
export const MAX_TITLE_LENGTH = 120;
export const MAX_DESCRIPTION_LENGTH = 400;
/** A stat value drawn as text - a rating, a band - stays a token rather than becoming a phrase. */
export const MAX_STAT_VALUE_LENGTH = 16;
/** A fact value is a short phrase - a city, a profile name - rather than a sentence. */
export const MAX_FACT_VALUE_LENGTH = 40;

// A meaning, not a colour: the components map each tone to design-system tokens.
export type AnswerTone = 'neutral' | 'info' | 'success' | 'warning' | 'error';

const TONES: readonly AnswerTone[] = [
  'neutral',
  'info',
  'success',
  'warning',
  'error',
];

/** How values are written on axes, labels and tooltips; not a Kendo format string. */
export type AnswerValueFormat = 'number' | 'currency' | 'percent';

const VALUE_FORMATS: readonly AnswerValueFormat[] = [
  'number',
  'currency',
  'percent',
];

/**
 * A caption under a widget. Toned, it is the verdict on the widget's values - within tolerance, a
 * gap to close - and is drawn with an icon in the tone's colour; `neutral` is a plain caption.
 */
export interface AnswerFootnote {
  text: string;
  tone: AnswerTone;
}

export interface AnswerChip {
  label: string;
  tone: AnswerTone;
}

export interface AnswerChipList {
  kind: 'chiplist';
  title?: string;
  items: AnswerChip[];
}

export interface AnswerChartSeries {
  name: string;
  // `null` is a gap: Kendo leaves the point out.
  data: (number | null)[];
  /** Set only when the series means good or bad; otherwise it takes its categorical hue. */
  tone?: AnswerTone;
}

interface AnswerChartCommon {
  kind: 'chart';
  title?: string;
  valueFormat: AnswerValueFormat;
  footnote?: AnswerFootnote;
}

/** `horizontal` lays a bar chart's categories down the side, the shape a ranking reads best in. */
export type AnswerChartOrientation = 'vertical' | 'horizontal';

export interface AnswerCategoryChart extends AnswerChartCommon {
  /** `bar` is drawn as vertical Kendo columns - the shape people mean by "bar chart". */
  type: 'bar' | 'line';
  /** Bars only; a line is always drawn over a horizontal axis. Vertical when left out. */
  orientation?: AnswerChartOrientation;
  /** Bars only: the series piled into one bar per category rather than drawn side by side. */
  stacked?: boolean;
  categories: string[];
  series: AnswerChartSeries[];
}

export interface AnswerPieSlice {
  label: string;
  value: number;
  /** Set only when the slice means good or bad; otherwise it takes its categorical hue. */
  tone?: AnswerTone;
}

export interface AnswerPieChart extends AnswerChartCommon {
  type: 'pie';
  slices: AnswerPieSlice[];
}

export type AnswerChart = AnswerCategoryChart | AnswerPieChart;

export interface AnswerCard {
  title: string;
  description?: string;
  chips?: AnswerChipList;
}

/** Columns the widest layout uses; a narrow panel still stacks them. */
export type AnswerCardColumns = 1 | 2 | 3 | 4;

export interface AnswerCardGroup {
  kind: 'cards';
  columns: AnswerCardColumns;
  cards: AnswerCard[];
}

export interface AnswerTimelineEvent {
  // Local midnight for a date-only string: `new Date('2026-03-04')` is midnight UTC, the day
  // before anywhere west of Greenwich.
  date: Date;
  title: string;
  subtitle?: string;
  description?: string;
  /** Colours the event's marker. `neutral` when the event carries no judgement. */
  tone: AnswerTone;
}

/** Chronological order, which the parser applies before Kendo sees the events. */
export type AnswerTimelineOrder = 'asc' | 'desc';

export interface AnswerTimeline {
  kind: 'timeline';
  title?: string;
  order: AnswerTimelineOrder;
  events: AnswerTimelineEvent[];
}

export interface AnswerStat {
  label: string;
  /** A number is written through `valueFormat`; a short string is drawn as it arrived. */
  value: number | string;
  valueFormat: AnswerValueFormat;
  tone: AnswerTone;
  /** A line under the value - what it is measured against, or where it moved from. */
  caption?: string;
}

export interface AnswerStatGroup {
  kind: 'stats';
  title?: string;
  items: AnswerStat[];
  footnote?: AnswerFootnote;
}

/** One row of a checklist: what it is, why it stands where it does, and the status it is at. */
export interface AnswerListItem {
  title: string;
  subtitle?: string;
  /** The status in words, drawn as a chip; the tone colours the chip and picks the row's icon. */
  status?: string;
  tone: AnswerTone;
}

export interface AnswerList {
  kind: 'list';
  title?: string;
  items: AnswerListItem[];
  footnote?: AnswerFootnote;
}

/** One fact about a record: a label and its value, which is formatted only when it is a number. */
export interface AnswerFact {
  label: string;
  value: number | string;
  valueFormat: AnswerValueFormat;
  tone: AnswerTone;
}

export interface AnswerFactList {
  kind: 'facts';
  title?: string;
  items: AnswerFact[];
  footnote?: AnswerFootnote;
}

export interface AnswerTableColumn {
  label: string;
  /** Set on a column of figures: its numbers are formatted and the column is right-aligned. */
  valueFormat?: AnswerValueFormat;
}

/** `null` is an empty cell, drawn blank rather than as a zero or a dash the answer did not write. */
export type AnswerTableCell = number | string | null;

export interface AnswerTable {
  kind: 'table';
  title?: string;
  columns: AnswerTableColumn[];
  /** Every row has exactly one cell per column; the parser pads and trims them. */
  rows: AnswerTableCell[][];
  footnote?: AnswerFootnote;
}

/** One web page the answer cites, numbered as its inline markers name it. */
export interface AnswerSource {
  id: number;
  title: string;
  /** Always `http:` or `https:`; anything else is dropped before it can become a link. */
  url: string;
  summary?: string;
  date?: string;
}

/** The list of web sources under an answer: the `nxsources` block the web citation prompt asks for. */
export interface AnswerSources {
  kind: 'sources';
  items: AnswerSource[];
}

/** What a suggested action creates in the CRM once the advisor confirms it. */
export type AnswerActionType = 'task' | 'appointment' | 'note';

export interface AnswerAction {
  type: AnswerActionType;
  title: string;
  description?: string;
  /** The fact in the data that calls for the action: the card's caption. */
  reason?: string;
  /** A day the data named; it wins over `inDays`. */
  date?: Date;
  /** Days from the day the card is drawn, when the data named no day. */
  inDays?: number;
  /** `HH:mm`, appointments only. */
  time?: string;
  /** The entry the record attaches to, kept only when the action's type can attach to its kind. */
  withEntityKey?: string;
  withType?: EntryType;
  /**
   * The item's place in the block as written, dropped items included: a created record is saved
   * under it, so the place does not move when the parser drops an item it used to keep.
   */
  index?: number;
}

/** The next steps under an answer: the `nxactions` block the suggested-actions prompt asks for. */
export interface AnswerActions {
  kind: 'actions';
  items: AnswerAction[];
}

/** The questions to ask next under an answer: the `nxfollowups` block the follow-up prompt asks for. */
export interface AnswerFollowUps {
  kind: 'followups';
  items: string[];
}

export type AnswerStackOrientation = 'horizontal' | 'vertical';

// No timeline (it needs the full width), no profile (it opens an answer), no table (its columns
// need the width too), no stack in a stack.
export type AnswerStackItem =
  | AnswerChart
  | AnswerCardGroup
  | AnswerChipList
  | AnswerStatGroup
  | AnswerList
  | AnswerFactList;

export interface AnswerStack {
  kind: 'stack';
  orientation: AnswerStackOrientation;
  items: AnswerStackItem[];
}

/** The one client, contact or household an answer is about, drawn as the card that opens it. */
export interface AnswerProfile {
  kind: 'profile';
  /** Who the card is about. The avatar takes its initials from it. */
  name: string;
  /**
   * The address-book key the avatar loads the photo with. It is never drawn, and it is only kept
   * when it is one, so a string the model made up never becomes a request.
   */
  key?: string;
  subtitle?: string;
  /** Short facts, drawn on one line under the name. */
  details: string[];
  /** What kind of card this is, in the answer's words; drawn as an `info` chip by the name. */
  badge?: string;
  chips?: AnswerChipList;
  description?: string;
}

export type AnswerWidget =
  | AnswerChart
  | AnswerCardGroup
  | AnswerChipList
  | AnswerTimeline
  | AnswerStatGroup
  | AnswerStack
  | AnswerProfile
  | AnswerList
  | AnswerFactList
  | AnswerTable
  | AnswerSources
  | AnswerActions
  | AnswerFollowUps;

export function isAnswerWidgetLanguage(
  value?: string | null,
): value is AnswerWidgetLanguage {
  return (
    !!value && (ANSWER_WIDGET_LANGUAGES as readonly string[]).includes(value)
  );
}

/**
 * Reads one widget payload. Returns `null` when the JSON is invalid or the payload does not carry
 * enough to draw - the caller then leaves the block as markdown.
 */
export function parseAnswerWidget(
  language: AnswerWidgetLanguage,
  source: string,
): AnswerWidget | null {
  const payload = parseJsonObject(source);
  if (!payload) {
    return null;
  }

  switch (language) {
    case 'nxchart':
      return parseChart(payload);
    case 'nxcard':
      return parseCardGroup(payload);
    case 'nxchiplist':
      return parseChipList(payload);
    case 'nxtimeline':
      return parseTimeline(payload);
    case 'nxstats':
      return parseStatGroup(payload);
    case 'nxstack':
      return parseStack(payload);
    case 'nxprofile':
      return parseProfile(payload);
    case 'nxlist':
      return parseList(payload);
    case 'nxfacts':
      return parseFactList(payload);
    case 'nxtable':
      return parseTable(payload);
    case 'nxsources':
      return parseSources(payload);
    case 'nxactions':
      return parseActions(payload);
    case 'nxfollowups':
      return parseFollowUps(payload);
    default:
      return null;
  }
}

function parseJsonObject(source: string): Record<string, unknown> | null {
  const trimmed = source.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

const CHART_ORIENTATIONS: readonly AnswerChartOrientation[] = [
  'vertical',
  'horizontal',
];

function parseChart(payload: Record<string, unknown>): AnswerChart | null {
  const common: AnswerChartCommon = {
    kind: 'chart',
    title: optionalText(payload['title'], MAX_TITLE_LENGTH),
    valueFormat: oneOf(payload['valueFormat'], VALUE_FORMATS, 'number'),
    footnote: parseFootnote(payload['footnote']),
  };

  if (payload['type'] === 'pie') {
    const slices = parsePieSlices(payload['slices'] ?? payload['data']);
    return slices.length ? { ...common, type: 'pie', slices } : null;
  }

  if (payload['type'] !== 'bar' && payload['type'] !== 'line') {
    return null;
  }

  const categories = toArray(payload['categories'])
    .slice(0, MAX_CHART_POINTS)
    .map((category) => optionalText(category, MAX_LABEL_LENGTH) ?? '');
  const series = parseChartSeries(payload['series'], categories.length);

  if (!categories.length || !series.length) {
    return null;
  }

  // A line is always drawn over a horizontal axis: turned or stacked, it would be a bar chart.
  const bars = payload['type'] === 'bar';

  return {
    ...common,
    type: payload['type'],
    orientation: bars
      ? oneOf(payload['orientation'], CHART_ORIENTATIONS, 'vertical')
      : 'vertical',
    stacked: bars && toBoolean(payload['stack']),
    categories,
    series,
  };
}

/**
 * Series are truncated to the category count so a short row never shifts values onto the wrong
 * category, and a series without a single usable number is dropped instead of drawn empty.
 */
function parseChartSeries(
  value: unknown,
  categoryCount: number,
): AnswerChartSeries[] {
  return toArray(value)
    .slice(0, MAX_CHART_SERIES)
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const data = toArray(raw['data'])
        .slice(0, categoryCount)
        .map((point) => toNumber(point));

      if (!data.some((point) => point !== null)) {
        return [];
      }

      return [
        {
          name: optionalText(raw['name'], MAX_LABEL_LENGTH) ?? '',
          data,
          tone: optionalOneOf(raw['tone'], TONES),
        },
      ];
    });
}

function parsePieSlices(value: unknown): AnswerPieSlice[] {
  return toArray(value)
    .slice(0, MAX_PIE_SLICES)
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const label = optionalText(
        raw['label'] ?? raw['category'],
        MAX_LABEL_LENGTH,
      );
      const sliceValue = toNumber(raw['value']);

      // A share of nothing is no slice: a zero would still take a colour and a legend row.
      if (!label || sliceValue === null || sliceValue <= 0) {
        return [];
      }

      return [
        { label, value: sliceValue, tone: optionalOneOf(raw['tone'], TONES) },
      ];
    });
}

// A plain string is a caption; the object form carries the tone of a verdict.
function parseFootnote(value: unknown): AnswerFootnote | undefined {
  if (isRecord(value)) {
    const text = optionalText(
      value['text'] ?? value['label'],
      MAX_DESCRIPTION_LENGTH,
    );
    return text
      ? { text, tone: oneOf(value['tone'], TONES, 'neutral') }
      : undefined;
  }

  const text = optionalText(value, MAX_DESCRIPTION_LENGTH);
  return text ? { text, tone: 'neutral' } : undefined;
}

function parseCardGroup(
  payload: Record<string, unknown>,
): AnswerCardGroup | null {
  const cards = toArray(payload['cards'])
    .slice(0, MAX_CARDS)
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const title = optionalText(raw['title'], MAX_TITLE_LENGTH);
      if (!title) {
        return [];
      }

      const chips = isRecord(raw['chips']) ? parseChipList(raw['chips']) : null;

      return [
        {
          title,
          description: optionalText(raw['description'], MAX_DESCRIPTION_LENGTH),
          chips: chips ?? undefined,
        },
      ];
    });

  if (!cards.length) {
    return null;
  }

  return {
    kind: 'cards',
    columns: parseColumns(payload['columns'], cards.length),
    cards,
  };
}

// Without a usable count the cards fill the row; never more columns than cards nor than the
// widest layout, so an out-of-range count is fitted rather than rejected.
function parseColumns(value: unknown, cardCount: number): AnswerCardColumns {
  const columns = toNumber(value);
  // Nothing readable, or a count no layout could have, is treated as none given rather than as one.
  const requested =
    columns === null || columns < 1 ? cardCount : Math.trunc(columns);

  return Math.min(requested, cardCount, MAX_CARD_COLUMNS) as AnswerCardColumns;
}

function parseChipList(
  payload: Record<string, unknown>,
): AnswerChipList | null {
  const items = toArray(payload['items'])
    .slice(0, MAX_CHIPS)
    .flatMap((raw) => {
      const label = isRecord(raw)
        ? optionalText(raw['label'], MAX_LABEL_LENGTH)
        : optionalText(raw, MAX_LABEL_LENGTH);

      if (!label) {
        return [];
      }

      const tone = isRecord(raw)
        ? oneOf(raw['tone'], TONES, 'neutral')
        : 'neutral';

      return [{ label, tone }];
    });

  if (!items.length) {
    return null;
  }

  return {
    kind: 'chiplist',
    title: optionalText(payload['title'], MAX_TITLE_LENGTH),
    items,
  };
}

const TIMELINE_ORDERS: readonly AnswerTimelineOrder[] = ['asc', 'desc'];

// Sorted here rather than by the renderer, so each marker's tone stays with its own event.
function parseTimeline(
  payload: Record<string, unknown>,
): AnswerTimeline | null {
  const order = oneOf(payload['order'], TIMELINE_ORDERS, 'asc');
  const direction = order === 'asc' ? 1 : -1;

  const events = toArray(payload['events'])
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const title = optionalText(raw['title'], MAX_TITLE_LENGTH);
      const date = toDate(raw['date']);

      if (!title || !date) {
        return [];
      }

      return [
        {
          date,
          title,
          subtitle: optionalText(raw['subtitle'], MAX_LABEL_LENGTH),
          description: optionalText(raw['description'], MAX_DESCRIPTION_LENGTH),
          tone: oneOf(raw['tone'], TONES, 'neutral'),
        },
      ];
    })
    .sort(
      (left, right) => direction * (left.date.getTime() - right.date.getTime()),
    )
    .slice(0, MAX_TIMELINE_EVENTS);

  if (events.length < MIN_TIMELINE_EVENTS) {
    return null;
  }

  return {
    kind: 'timeline',
    title: optionalText(payload['title'], MAX_TITLE_LENGTH),
    order,
    events,
  };
}

function parseStatGroup(
  payload: Record<string, unknown>,
): AnswerStatGroup | null {
  const items = toArray(payload['items'])
    .slice(0, MAX_STATS)
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const label = optionalText(raw['label'], MAX_LABEL_LENGTH);
      const value = toStatValue(raw['value']);

      if (!label || value === null) {
        return [];
      }

      return [
        {
          label,
          value,
          valueFormat: oneOf(raw['valueFormat'], VALUE_FORMATS, 'number'),
          tone: oneOf(raw['tone'], TONES, 'neutral'),
          caption: optionalText(raw['caption'], MAX_LABEL_LENGTH),
        },
      ];
    });

  return items.length >= MIN_STATS
    ? {
        kind: 'stats',
        title: optionalText(payload['title'], MAX_TITLE_LENGTH),
        items,
        footnote: parseFootnote(payload['footnote']),
      }
    : null;
}

/** A number keeps its format; anything else has to already read as a value on its own. */
function toStatValue(value: unknown): number | string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const numeric = toNumber(value);
  return numeric ?? optionalText(value, MAX_STAT_VALUE_LENGTH) ?? null;
}

// A row needs a title; its status and the reason for it are optional, so a bare checklist reads.
function parseList(payload: Record<string, unknown>): AnswerList | null {
  const items = toArray(payload['items'])
    .slice(0, MAX_LIST_ITEMS)
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const title = optionalText(
        raw['title'] ?? raw['label'],
        MAX_LABEL_LENGTH,
      );
      if (!title) {
        return [];
      }

      return [
        {
          title,
          subtitle: optionalText(raw['subtitle'], MAX_TITLE_LENGTH),
          status: optionalText(raw['status'], MAX_LABEL_LENGTH),
          tone: oneOf(raw['tone'], TONES, 'neutral'),
        },
      ];
    });

  if (items.length < MIN_LIST_ITEMS) {
    return null;
  }

  return {
    kind: 'list',
    title: optionalText(payload['title'], MAX_TITLE_LENGTH),
    items,
    footnote: parseFootnote(payload['footnote']),
  };
}

// Unlike a stat, a fact written as a string stays a string: "2019" is a year, not two thousand
// and nineteen of something, and only a JSON number asks to be formatted.
function parseFactList(
  payload: Record<string, unknown>,
): AnswerFactList | null {
  const items = toArray(payload['items'])
    .slice(0, MAX_FACTS)
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const label = optionalText(raw['label'], MAX_LABEL_LENGTH);
      const value = toNumberOrText(raw['value'], MAX_FACT_VALUE_LENGTH);

      if (!label || value === null) {
        return [];
      }

      return [
        {
          label,
          value,
          valueFormat: oneOf(raw['valueFormat'], VALUE_FORMATS, 'number'),
          tone: oneOf(raw['tone'], TONES, 'neutral'),
        },
      ];
    });

  if (items.length < MIN_FACTS) {
    return null;
  }

  return {
    kind: 'facts',
    title: optionalText(payload['title'], MAX_TITLE_LENGTH),
    items,
    footnote: parseFootnote(payload['footnote']),
  };
}

// Columns are kept in place even without a label, since dropping one would shift every row's
// cells under the wrong heading; rows are trimmed and padded to the columns for the same reason.
function parseTable(payload: Record<string, unknown>): AnswerTable | null {
  const columns: AnswerTableColumn[] = toArray(payload['columns'])
    .slice(0, MAX_TABLE_COLUMNS)
    .map((raw) =>
      isRecord(raw)
        ? {
            label: optionalText(raw['label'], MAX_LABEL_LENGTH) ?? '',
            valueFormat: optionalOneOf(raw['valueFormat'], VALUE_FORMATS),
          }
        : {
            label: optionalText(raw, MAX_LABEL_LENGTH) ?? '',
            valueFormat: undefined,
          },
    );

  if (
    columns.length < MIN_TABLE_COLUMNS ||
    columns.every((column) => !column.label)
  ) {
    return null;
  }

  const rows = toArray(payload['rows'])
    .slice(0, MAX_TABLE_ROWS)
    .flatMap((raw) => {
      const cells = columns.map((column, index) =>
        toCell(toArray(raw)[index], column),
      );

      return cells.some((cell) => cell !== null) ? [cells] : [];
    });

  if (rows.length < MIN_TABLE_ROWS) {
    return null;
  }

  return {
    kind: 'table',
    title: optionalText(payload['title'], MAX_TITLE_LENGTH),
    columns,
    rows,
    footnote: parseFootnote(payload['footnote']),
  };
}

// A column of figures reads a numeric string as its number; elsewhere a string is text as written.
function toCell(value: unknown, column: AnswerTableColumn): AnswerTableCell {
  if (column.valueFormat) {
    const numeric = toNumber(value);
    if (numeric !== null) {
      return numeric;
    }
  }

  return toNumberOrText(value, MAX_LABEL_LENGTH);
}

/** A JSON number as it is; anything else as text, cut to a length; nothing usable as `null`. */
function toNumberOrText(
  value: unknown,
  maxLength: number,
): number | string | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  return optionalText(value, maxLength) ?? null;
}

// A source needs a title and a web URL; its number is the one the markers in the prose use, so a
// missing or repeated number takes the next free one rather than dropping the source.
function parseSources(payload: Record<string, unknown>): AnswerSources | null {
  const taken = new Set<number>();
  let next = 1;

  const items = toArray(payload['items'])
    .slice(0, MAX_SOURCES)
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const title = optionalText(raw['title'], MAX_TITLE_LENGTH);
      const url = toHttpUrl(raw['url'] ?? raw['link']);

      if (!title || !url) {
        return [];
      }

      const requested = toNumber(raw['id']);
      let id =
        requested !== null && Number.isInteger(requested) && requested > 0
          ? requested
          : 0;
      if (!id || taken.has(id)) {
        while (taken.has(next)) {
          next++;
        }
        id = next;
      }
      taken.add(id);

      return [
        {
          id,
          title,
          url,
          summary: optionalText(
            raw['summary'] ?? raw['snippet'],
            MAX_DESCRIPTION_LENGTH,
          ),
          date: optionalText(
            raw['date'] ?? raw['publishedDate'],
            MAX_SOURCE_DATE_LENGTH,
          ),
        },
      ];
    })
    .sort((left, right) => left.id - right.id);

  return items.length ? { kind: 'sources', items } : null;
}

/** A web address and nothing else: a `javascript:` or `data:` URL never becomes a link. */
function toHttpUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}

const ACTION_TYPES: readonly AnswerActionType[] = [
  'task',
  'appointment',
  'note',
];

// The entries the create API takes for each action as it is; a task or an appointment about an
// opportunity or a case also needs the record's parent entry, which is not read yet.
const ACTION_ENTRY_TYPES: Readonly<
  Record<AnswerActionType, readonly EntryType[]>
> = {
  task: ['addressBook', 'lead'],
  appointment: ['addressBook', 'lead'],
  note: ['addressBook', 'lead', 'opportunity', 'case'],
};

// The actions that stand without an entry: a to-do can be the advisor's own.
const PERSONAL_ACTION_TYPES: ReadonlySet<AnswerActionType> =
  new Set<AnswerActionType>(['task']);

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// One the CRM could not create (no type, no title, or no entry the type needs) is dropped, then
// the first three are drawn. `email` is not offered yet, so it goes the way of any unknown type.
function parseActions(payload: Record<string, unknown>): AnswerActions | null {
  const items = toArray(payload['items'])
    .flatMap((raw, index) => {
      const action = isRecord(raw) ? parseAction(raw, index) : null;
      return action ? [action] : [];
    })
    .slice(0, MAX_ACTIONS);

  return items.length ? { kind: 'actions', items } : null;
}

function parseAction(
  raw: Record<string, unknown>,
  index: number,
): AnswerAction | null {
  const type = optionalOneOf(raw['type'], ACTION_TYPES);
  const title = actionText(raw['title'], MAX_LABEL_LENGTH);
  if (!type || !title) {
    return null;
  }

  // The key says what the entry is; a `withType` that disagrees means the model mixed records
  // up, and the key is not trusted.
  const entry = toEntryKey(raw['withEntityKey'], ACTION_ENTRY_TYPES[type]);
  const claimedType = optionalText(raw['withType'], MAX_LABEL_LENGTH);
  const attached =
    entry && (!claimedType || claimedType === entry.type) ? entry : undefined;
  if (!attached && !PERSONAL_ACTION_TYPES.has(type)) {
    return null;
  }

  // A note is stamped when it is created, so it keeps neither a date nor a number of days.
  const dated = type !== 'note';
  const date = dated ? toActionDate(raw['date']) : undefined;

  return {
    type,
    title,
    description: actionText(raw['description'], MAX_DESCRIPTION_LENGTH),
    reason: actionText(raw['reason'], MAX_LABEL_LENGTH),
    date,
    inDays: dated && !date ? toInDays(raw['inDays']) : undefined,
    time:
      type === 'appointment'
        ? (toTime(raw['time']) ?? writtenTime(raw['date'], date))
        : undefined,
    withEntityKey: attached?.key,
    withType: attached?.type,
    index,
  };
}

// The day of a leading YYYY-MM-DD in the reader's zone, so a timestamp keeps the day written on it;
// a date written any other way is not guessed at.
function toActionDate(value: unknown): Date | undefined {
  const day =
    typeof value === 'string'
      ? /^\s*(\d{4}-\d{2}-\d{2})(?!\d)/.exec(value)?.[1]
      : undefined;

  return parseDateOnlyLocal(day) ?? undefined;
}

// The HH:mm written after a day that was read, for a model that put the time in the date.
function writtenTime(
  value: unknown,
  day: Date | undefined,
): string | undefined {
  const time =
    day && typeof value === 'string'
      ? /^\s*\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2})/.exec(value)?.[1]
      : undefined;

  return toTime(time);
}

// A citation links the answer to a record; copied into the record it creates, it is literal markup.
function actionText(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const text = plainText(
    AiMarkupUtils.removeCRMObjects(
      AiMarkupUtils.removeMaximizerReferences(value),
    ),
  )?.replaceAll(/ (?=[.,;:!?])/g, '');

  return text ? cut(text, maxLength) : undefined;
}

function toInDays(value: unknown): number | undefined {
  const days = toNumber(value);

  return days !== null &&
    Number.isInteger(days) &&
    days >= 0 &&
    days <= MAX_ACTION_IN_DAYS
    ? days
    : undefined;
}

function toTime(value: unknown): string | undefined {
  const time = typeof value === 'string' ? value.trim() : '';

  return TIME_PATTERN.test(time) ? time : undefined;
}

// Up to three questions, each as the reader would type it. A repeat, whatever its case or final
// punctuation, is dropped, as is a question too long for a chip and anything that is not text.
function parseFollowUps(
  payload: Record<string, unknown>,
): AnswerFollowUps | null {
  const seen = new Set<string>();

  const items = toArray(payload['items'])
    .flatMap((raw) => {
      const question = plainText(
        isRecord(raw) ? (raw['question'] ?? raw['text']) : raw,
      );
      if (!question || question.length > MAX_FOLLOW_UP_LENGTH) {
        return [];
      }
      // The lookbehind starts the match at the first final mark, which keeps the match linear.
      const repeated = question
        .toLowerCase()
        .replace(/(?<![\s?.!])[\s?.!]+$/, '');
      if (!repeated || seen.has(repeated)) {
        return [];
      }
      seen.add(repeated);
      return [question];
    })
    .slice(0, MAX_FOLLOW_UPS);

  return items.length ? { kind: 'followups', items } : null;
}

/**
 * The widgets a stack may hold, by the names written on a fence; the others are left out on
 * purpose, see {@link AnswerStackItem}.
 */
const STACK_ITEM_PARSERS: Record<
  string,
  (payload: Record<string, unknown>) => AnswerStackItem | null
> = {
  nxchart: parseChart,
  nxcard: parseCardGroup,
  nxchiplist: parseChipList,
  nxstats: parseStatGroup,
  nxlist: parseList,
  nxfacts: parseFactList,
};

const STACK_ORIENTATIONS: readonly AnswerStackOrientation[] = [
  'horizontal',
  'vertical',
];

// An item a stack cannot hold, or that does not parse on its own, is dropped; with fewer than
// two left, the block falls back to markdown.
function parseStack(payload: Record<string, unknown>): AnswerStack | null {
  const items = toArray(payload['items'])
    .slice(0, MAX_STACK_ITEMS)
    .flatMap((raw) => {
      if (!isRecord(raw)) {
        return [];
      }

      const parse =
        STACK_ITEM_PARSERS[optionalText(raw['widget'], MAX_LABEL_LENGTH) ?? ''];
      const item = parse?.(raw) ?? null;

      return item ? [item] : [];
    });

  if (items.length < MIN_STACK_ITEMS) {
    return null;
  }

  return {
    kind: 'stack',
    orientation: oneOf(
      payload['orientation'],
      STACK_ORIENTATIONS,
      'horizontal',
    ),
    items,
  };
}

// A name is all a profile needs: an unusable part is left out rather than the whole card.
function parseProfile(payload: Record<string, unknown>): AnswerProfile | null {
  const name = optionalText(payload['name'], MAX_TITLE_LENGTH);
  if (!name) {
    return null;
  }

  const details = toArray(payload['details'])
    .slice(0, MAX_PROFILE_DETAILS)
    .flatMap((raw) => {
      const detail = optionalText(raw, MAX_LABEL_LENGTH);
      return detail ? [detail] : [];
    });

  const chips = isRecord(payload['chips'])
    ? parseChipList(payload['chips'])
    : null;

  return {
    kind: 'profile',
    name,
    key: toEntryKey(payload['key'], ['addressBook'])?.key,
    subtitle: optionalText(payload['subtitle'], MAX_LABEL_LENGTH),
    details,
    badge: optionalText(payload['badge'], MAX_LABEL_LENGTH),
    chips: chips ?? undefined,
    description: optionalText(payload['description'], MAX_DESCRIPTION_LENGTH),
  };
}

/** Base64, the alphabet record keys are written in. It has no dot, so no `../` in a photo URL. */
const BASE64_TOKEN = /^[A-Za-z0-9+/]+={0,2}$/;

interface EntryKey {
  key: string;
  type: EntryType;
}

// A key turns into a request (the avatar's photo, the entry a record is created on), so only one
// the CRM resolves to an entry of an allowed type is kept.
function toEntryKey(
  value: unknown,
  allowed: readonly EntryType[],
): EntryKey | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const key = value.trim();
  if (!BASE64_TOKEN.test(key)) {
    return undefined;
  }

  const type = Octopus.resolveEntryTypeFromKey(key);

  return type && allowed.includes(type) ? { key, type } : undefined;
}

function toArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** Numbers may arrive as strings; anything that is not finite is a gap, never a zero. */
function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim().replaceAll(',', ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

/** `true` as JSON or as the word; anything else, including nothing, is `false`. */
function toBoolean(value: unknown): boolean {
  return (
    value === true ||
    (typeof value === 'string' && value.trim().toLowerCase() === 'true')
  );
}

// Date-only strings are rebuilt in the reader's zone; impossible days, which `Date` rolls over
// silently, are rejected.
function toDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());

  if (!dateOnly) {
    const parsed = new Date(value.trim());
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const [year, month, day] = dateOnly.slice(1).map(Number);
  const parsed = new Date(year, month - 1, day);

  const roundTrips =
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day;

  return roundTrips ? parsed : null;
}

function optionalText(value: unknown, maxLength: number): string | undefined {
  const text = plainText(value);
  return text ? cut(text, maxLength) : undefined;
}

function cut(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

function plainText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  return value.replaceAll(/\s+/g, ' ').trim() || undefined;
}

function oneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return optionalOneOf(value, allowed) ?? fallback;
}

function optionalOneOf<T extends string>(
  value: unknown,
  allowed: readonly T[],
): T | undefined {
  return typeof value === 'string' &&
    (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}
